import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { WebSocketService } from './websocket.service';

/**
 * Ticket update event from WebSocket
 */
export interface TicketUpdateEvent {
  ticketId: string;
  eventType: string;
  changes?: {
    status?: { old: string; new: string };
    priority?: { old: string; new: string };
    assignee?: { name: string; email: string };
  };
  timestamp: string;
}

/**
 * Ticket comment event from WebSocket
 */
export interface TicketCommentEvent {
  ticketId: string;
  comment: {
    id: string;
    content: string;
    author: string;
    authorType: 'user' | 'agent' | 'system';
    isPublic: boolean;
    createdAt: string;
  };
  timestamp: string;
}

/**
 * Ticket assignment event from WebSocket
 */
export interface TicketAssignedEvent {
  ticketId: string;
  assignee: {
    name: string;
    email: string;
  };
  timestamp: string;
}

/**
 * Ticket status change event from WebSocket
 */
export interface TicketStatusChangedEvent {
  ticketId: string;
  oldStatus: string;
  newStatus: string;
  changedBy?: string;
  timestamp: string;
}

/**
 * Ticket attachment event from WebSocket
 */
export interface TicketAttachmentEvent {
  ticketId: string;
  attachment: {
    id: string;
    fileName: string;
    fileSize: number | null;
    formattedFileSize: string;
    fileType: string | null;
    fileExtension: string | null;
    downloadUrl: string | null;
    isImage: boolean;
    isPdf: boolean;
    fileIcon: string;
    createdAt: string;
  };
  timestamp: string;
}

/**
 * Ticket attachment deleted event from WebSocket
 */
export interface TicketAttachmentDeletedEvent {
  ticketId: string;
  attachmentId: string;
  timestamp: string;
}

/**
 * Ticket Real-time Service
 *
 * Provides real-time updates for support tickets via WebSocket.
 * Subscribe to specific ticket events or all ticket updates.
 *
 * Usage:
 * ```typescript
 * // In component
 * this.ticketRealtime.onTicketUpdated('ticket-id-123').subscribe(event => {
 *   console.log('Ticket updated:', event);
 *   this.refreshTicket();
 * });
 *
 * // Or for all tickets in the organization
 * this.ticketRealtime.ticketUpdates$.subscribe(event => {
 *   console.log('Any ticket updated:', event);
 * });
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class TicketRealtimeService implements OnDestroy {
  private destroy$ = new Subject<void>();

  // All ticket update events
  private ticketUpdatesSubject = new BehaviorSubject<TicketUpdateEvent | null>(null);
  public ticketUpdates$: Observable<TicketUpdateEvent | null> = this.ticketUpdatesSubject.asObservable();

  // All ticket comment events
  private ticketCommentsSubject = new BehaviorSubject<TicketCommentEvent | null>(null);
  public ticketComments$: Observable<TicketCommentEvent | null> = this.ticketCommentsSubject.asObservable();

  // All ticket assignment events
  private ticketAssignmentsSubject = new BehaviorSubject<TicketAssignedEvent | null>(null);
  public ticketAssignments$: Observable<TicketAssignedEvent | null> = this.ticketAssignmentsSubject.asObservable();

  // All ticket status change events
  private ticketStatusChangesSubject = new BehaviorSubject<TicketStatusChangedEvent | null>(null);
  public ticketStatusChanges$: Observable<TicketStatusChangedEvent | null> = this.ticketStatusChangesSubject.asObservable();

  // All ticket attachment events
  private ticketAttachmentsSubject = new BehaviorSubject<TicketAttachmentEvent | null>(null);
  public ticketAttachments$: Observable<TicketAttachmentEvent | null> = this.ticketAttachmentsSubject.asObservable();

  // All ticket attachment deleted events
  private ticketAttachmentDeletedSubject = new BehaviorSubject<TicketAttachmentDeletedEvent | null>(null);
  public ticketAttachmentDeleted$: Observable<TicketAttachmentDeletedEvent | null> = this.ticketAttachmentDeletedSubject.asObservable();

  // Track active subscriptions
  private isSubscribed = false;

  constructor(private websocketService: WebSocketService) {
    // Subscribe to WebSocket events when connection state changes
    this.websocketService.connectionState$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(state => {
      if (state === 'authenticated' && !this.isSubscribed) {
        this.subscribeToEvents();
      } else if (state === 'disconnected') {
        this.isSubscribed = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Subscribe to WebSocket ticket events
   */
  private subscribeToEvents(): void {
    if (this.isSubscribed) return;
    this.isSubscribed = true;

    // ticket.updated events
    this.websocketService.on<TicketUpdateEvent>('ticket.updated').pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      console.log('[TicketRealtime] Received ticket.updated:', event);
      this.ticketUpdatesSubject.next(event);
    });

    // ticket.comment events
    this.websocketService.on<TicketCommentEvent>('ticket.comment').pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      console.log('[TicketRealtime] Received ticket.comment:', event);
      this.ticketCommentsSubject.next(event);
    });

    // ticket.assigned events
    this.websocketService.on<TicketAssignedEvent>('ticket.assigned').pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      console.log('[TicketRealtime] Received ticket.assigned:', event);
      this.ticketAssignmentsSubject.next(event);
    });

    // ticket.statusChanged events
    this.websocketService.on<TicketStatusChangedEvent>('ticket.statusChanged').pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      console.log('[TicketRealtime] Received ticket.statusChanged:', event);
      this.ticketStatusChangesSubject.next(event);
    });

    // ticket.attachment events
    this.websocketService.on<TicketAttachmentEvent>('ticket.attachment').pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      console.log('[TicketRealtime] Received ticket.attachment:', event);
      this.ticketAttachmentsSubject.next(event);
    });

    // ticket.attachmentDeleted events
    this.websocketService.on<TicketAttachmentDeletedEvent>('ticket.attachmentDeleted').pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      console.log('[TicketRealtime] Received ticket.attachmentDeleted:', event);
      this.ticketAttachmentDeletedSubject.next(event);
    });

    console.log('[TicketRealtime] Subscribed to ticket events');
  }

  /**
   * Get updates for a specific ticket
   */
  onTicketUpdated(ticketId: string): Observable<TicketUpdateEvent> {
    return this.ticketUpdates$.pipe(
      filter((event): event is TicketUpdateEvent => event !== null && event.ticketId === ticketId),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Get comments for a specific ticket
   */
  onTicketComment(ticketId: string): Observable<TicketCommentEvent> {
    return this.ticketComments$.pipe(
      filter((event): event is TicketCommentEvent => event !== null && event.ticketId === ticketId),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Get assignment events for a specific ticket
   */
  onTicketAssigned(ticketId: string): Observable<TicketAssignedEvent> {
    return this.ticketAssignments$.pipe(
      filter((event): event is TicketAssignedEvent => event !== null && event.ticketId === ticketId),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Get status change events for a specific ticket
   */
  onTicketStatusChanged(ticketId: string): Observable<TicketStatusChangedEvent> {
    return this.ticketStatusChanges$.pipe(
      filter((event): event is TicketStatusChangedEvent => event !== null && event.ticketId === ticketId),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Get attachment events for a specific ticket
   */
  onTicketAttachment(ticketId: string): Observable<TicketAttachmentEvent> {
    return this.ticketAttachments$.pipe(
      filter((event): event is TicketAttachmentEvent => event !== null && event.ticketId === ticketId),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Get attachment deleted events for a specific ticket
   */
  onTicketAttachmentDeleted(ticketId: string): Observable<TicketAttachmentDeletedEvent> {
    return this.ticketAttachmentDeleted$.pipe(
      filter((event): event is TicketAttachmentDeletedEvent => event !== null && event.ticketId === ticketId),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Get all updates for a specific ticket (combined stream)
   */
  onAnyTicketEvent(ticketId: string): Observable<TicketUpdateEvent | TicketCommentEvent | TicketAssignedEvent | TicketStatusChangedEvent> {
    type AnyTicketEvent = TicketUpdateEvent | TicketCommentEvent | TicketAssignedEvent | TicketStatusChangedEvent;

    return new Observable<AnyTicketEvent>(observer => {
      const subscriptions = [
        this.onTicketUpdated(ticketId).subscribe(event => observer.next(event)),
        this.onTicketComment(ticketId).subscribe(event => observer.next(event)),
        this.onTicketAssigned(ticketId).subscribe(event => observer.next(event)),
        this.onTicketStatusChanged(ticketId).subscribe(event => observer.next(event))
      ];

      return () => {
        subscriptions.forEach(sub => sub.unsubscribe());
      };
    }).pipe(takeUntil(this.destroy$));
  }
}
